import { useState, useEffect, useCallback } from 'react';
import { changeLogService } from '../services/api';
import { ChangeLog, ChangeLogEntityType } from '../types/mold';

export interface UseChangeLogsResult {
  changeLogs: ChangeLog[];
  total: number;
  loading: boolean;
  error: string | null;
  retry: () => void;
}

/**
 * Fetches change logs. When `entityId` is supplied, uses the scoped endpoint
 * GET /change-logs/{entityType}/{entityId} (server-side filtered); otherwise
 * falls back to GET /change-logs/{entityType}.
 */
export const useChangeLogs = (
  entityType: ChangeLogEntityType,
  limit: number = 10,
  offset: number = 0,
  entityId?: number,
): UseChangeLogsResult => {
  const [changeLogs, setChangeLogs] = useState<ChangeLog[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchChangeLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response =
        entityId !== undefined
          ? await changeLogService.getChangeLogsByEntityId(
              entityType,
              entityId,
              { limit, offset },
            )
          : await changeLogService.getChangeLogs(entityType, { limit, offset });
      setChangeLogs(response.data.items ?? []);
      setTotal(response.data.total ?? 0);
    } catch (err) {
      setError('Failed to fetch change logs');
      setChangeLogs([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId, limit, offset]);

  useEffect(() => {
    fetchChangeLogs();
  }, [fetchChangeLogs]);

  return { changeLogs, total, loading, error, retry: fetchChangeLogs };
};
